// AC calculator: which ACs to show for a room, and the one we recommend.
//
// Reads the live shop (catalogue.js, pricing.js), never a list of its own:
// every product shown here is the unit the shop actually sells online
// (onlineKit), at its price, with its stock.
//
// Rules (Oct 2026):
//   • An AC is shown only if it still covers the room on the city's hottest
//     afternoon, after losing 5% of its power for every °C above the
//     temperature its series keeps full power to (43 or 46 °C), and in the
//     monsoon. Real kW decides, never the "ton" on the box.
//   • The answer is the smallest size in which at least one model does that
//     (answerFor in data/acCalculatorContent). When only some models of that
//     size keep up, the fitting models of the next size count too: a bigger
//     3 Star is often cheaper than a smaller 5 Star.
//   • ONE clear recommendation, always a unit in stock (24-hour delivery):
//       – heating asked for: the Hot & Cold model;
//       – 4 hrs a day: the non-inverter, which is far cheaper to repair and
//         whose higher bill hardly matters at that use;
//       – otherwise the best value for money: the lowest price plus 5 years
//         of electricity, whatever the type.
//     If nothing that fits is in stock in the answer size, the next size up
//     that has one in stock.
//   • Beside it, the comparison: non-inverter, 3 Star and 5 Star of the same
//     tonnage, one each, in stock first. When a non-inverter exists in the
//     tonnage but cannot cool the room at the heat, it is still named, with
//     what it gives.
//   • No single split covers it: two ACs, or talk to our team. Above 3.5 TR in
//     one room: talk to our team only.

import { CATALOGUE } from '../data/catalogue';
import { SERIES } from '../data/highlights';
import { PRICES, STOCK } from '../data/pricing';
import { COLLECTIONS } from '../data/collections';
import { STATIONS, TARIFF, cityFor } from '../data/indiaClimate';
import { calcEnergy } from '../pages/ac-engine';
import * as HL from './heatLoad';
import { onlineKit } from './listing';
import { formatINR } from './quote';
import { answerFor, unitsFrom, tonLabel, degC } from '../data/acCalculatorContent';

// What data/acCalculatorContent.js needs to work things out.
export const ENGINE = { ...HL, STATIONS, TARIFF, cityFor, calcEnergy };

// Every model with its real kW and full-power temperature.
export const UNITS = unitsFrom(CATALOGUE, SERIES);
const UNIT = Object.fromEntries(UNITS.map((u) => [u.id, u]));

// Every size the shop sells, smallest first: [0.8, 1, 1.5, 1.8, 2, 2.5].
export const SIZES = [...new Set(UNITS.map((u) => u.soldAs))].sort((a, b) => a - b);

const LOW_USE_HOURS = 6; // at or below this (the "4 hrs a day" choice) the non-inverter is recommended

// The four kinds of AC the comparison is made of.
export const TYPE_LABEL = {
  fixed: 'Non-inverter',
  three: '3 Star inverter',
  five: '5 Star inverter',
  heat: 'Hot & Cold, 3 Star',
};
const TYPE_SHORT = { fixed: 'non-inverter', three: '3 Star', five: '5 Star', heat: 'Hot & Cold' };
export function typeOf(model) {
  if (!model.inverter) return 'fixed';
  if (model.series === 'FTHT') return 'heat';
  return model.stars >= 5 ? 'five' : 'three';
}

// The unit sold online for a model, in the shape ProductCard expects.
function variantOf(model) {
  const kit = onlineKit(model);
  const price = PRICES[model.id]?.[kit];
  if (price == null) return null;
  return {
    key: `${model.id}:${kit}`,
    model,
    kit,
    price,
    mrp: model.variants[kit].mrp,
    idu: model.variants[kit].idu,
    inStock: (STOCK[model.id]?.[kit] || 0) > 0,
  };
}

// Yearly electricity by the old calculator's ISEER method, worked out on the
// room's load, not the AC's size: an inverter AC slows down to match the
// room, so a bigger one does not use more for the same room. `share` is the
// part of the room one AC carries when two share it.
function yearlyBill(model, ctx, share = 1) {
  return calcEnergy({
    ton: ctx.need.loadTR * share,
    iseer: model.iseer,
    type: model.inverter ? 'inverter' : 'non-inverter',
    dailyHrs: ctx.hours,
    coolingMonths: ctx.mo,
    elecRate: ctx.rate,
    targetTempC: ctx.targetTemp,
    topFloor: ctx.floor === 'top',
    highSun: ctx.sun === 'W',
    poorInsul: false,
  }).yearly;
}

// What the unit really gives on the hottest afternoon, and what it costs to run.
function withRunning(v, ctx) {
  const u = UNIT[v.model.id];
  const yearly = yearlyBill(v.model, ctx);
  return {
    ...v,
    size: u.soldAs,
    type: typeOf(v.model),
    atPeak: HL.capacityTR(u.kw, u.full, ctx.need.peakC),
    yearly,
    fiveYear: v.price + yearly * 5,
  };
}

function shopUnits(units, ctx) {
  const ids = new Set(units.map((u) => u.id));
  return CATALOGUE.filter((m) => ids.has(m.id))
    .map(variantOf)
    .filter(Boolean)
    .map((v) => withRunning(v, ctx));
}

// In stock first, then the cheapest to own over 5 years, then the smaller size.
const stockThenValue = (a, b) => (b.inStock - a.inStock) || (a.fiveYear - b.fiveYear) || (a.size - b.size);
const byValue = (a, b) => (a.fiveYear - b.fiveYear) || (a.size - b.size);

// Why we recommend it, in two to four short lines.
function reasonsFor(rec, why, slots, ctx, ans, inStockInAnswer) {
  const n = ctx.need;
  const out = [];
  const others = slots.filter((s) => s.v.key !== rec.key && s.v.inStock && s.type !== 'heat').map((s) => s.v);
  // "the 3 Star", or "the 1.8 Ton 3 Star" when it is a different size from the one we recommend
  const name = (o) => `the ${o.size !== rec.size ? `${tonLabel(o.size)} ` : ''}${TYPE_SHORT[o.type]}`;
  const cap = (t) => t.charAt(0).toUpperCase() + t.slice(1);
  // Rated for the heat: the reason a smaller or 5 Star model can be enough.
  const u = UNIT[rec.model.id];
  const rated = n.peakC > 43 && u.full >= 46 && n.peakC <= u.full;
  if (why === 'heat') {
    out.push('Cools in summer and heats in winter, so it replaces a room heater as well.');
    const cool = [...others].sort(byValue)[0];
    if (cool && cool.price < rec.price) out.push(`${formatINR(rec.price - cool.price)} more than ${name(cool)} that only cools.`);
  } else if (why === 'lowuse') {
    out.push(`Best value at ${ctx.hours} hrs a day: a non-inverter is far cheaper to repair, and at this use the inverter's power saving is small.`);
    const inv = others.filter((o) => o.type !== 'fixed').sort(byValue)[0];
    if (inv) {
      const saves = rec.yearly - inv.yearly;
      const price = inv.price > rec.price ? `costs ${formatINR(inv.price - rec.price)} more and ` : inv.price < rec.price ? `is ${formatINR(rec.price - inv.price)} cheaper and ` : '';
      out.push(`${cap(name(inv))} ${price}saves about ${formatINR(saves)} a year, but inverter repairs cost far more.`);
    }
  } else {
    out.push(`Best value for money: the lowest cost over 5 years at ${ctx.hours} hrs a day, ${formatINR(rec.fiveYear)} for the AC and its electricity.`);
    const room = rated ? 2 : 3; // keep the list to four lines
    others.sort(byValue).forEach((o) => {
      if (out.length >= room) return;
      const saves = rec.yearly - o.yearly;
      if (o.price > rec.price) {
        out.push(saves > 0
          ? `${cap(name(o))} costs ${formatINR(o.price - rec.price)} more and saves only ${formatINR(saves)} a year: ${(((o.price - rec.price) / saves)).toFixed(1)} years to pay back.`
          : `${cap(name(o))} costs ${formatINR(o.price - rec.price)} more to buy and more to run.`);
      } else if (o.price < rec.price) {
        out.push(`${cap(name(o))} is ${formatINR(rec.price - o.price)} cheaper to buy but costs ${formatINR(o.yearly - rec.yearly)} more a year to run.`);
      } else {
        out.push(`${cap(name(o))} costs the same but ${formatINR(o.yearly - rec.yearly)} more a year to run.`);
      }
    });
  }
  if (rated) {
    out.push(`Keeps full power up to ${u.full}°C, so it loses nothing on a ${degC(n.peakC)} afternoon.`);
  }
  if (rec.size !== ans.size) {
    out.push(inStockInAnswer
      ? `A ${tonLabel(rec.size)}: it costs less over 5 years than the ${tonLabel(ans.size)} models that can cool this room.`
      : `No ${tonLabel(ans.size)} that can cool this room is in stock today, so the ${tonLabel(rec.size)}, which is.`);
  }
  out.push(`Gives ${rec.atPeak.toFixed(2)} TR at ${degC(n.peakC)}, enough for this room's ${n.loadTR.toFixed(2)} TR.`);
  return out;
}

// The best two-AC combination that covers the room: in stock first, then the
// smallest total size, then the most even split, then cheaper.
export function bestPair(need, ctx) {
  const pool = shopUnits(UNITS.filter((u) => u.inverter && u.soldAs >= 1), ctx);
  const capAt = (v, t) => { const u = UNIT[v.model.id]; return HL.capacityTR(u.kw, u.full, t); };
  const cover = (a, b) => capAt(a, need.peakC) + capAt(b, need.peakC) >= need.summerTR - 1e-9
    && capAt(a, need.monsoonC) + capAt(b, need.monsoonC) >= need.monsoonTR - 1e-9;
  let best = null;
  for (let i = 0; i < pool.length; i += 1) {
    for (let j = i; j < pool.length; j += 1) {
      const [a, b] = pool[i].size <= pool[j].size ? [pool[i], pool[j]] : [pool[j], pool[i]];
      if (!cover(a, b)) continue;
      const c = {
        sizes: [a.size, b.size], total: a.size + b.size, units: [a, b], price: a.price + b.price,
        inStock: a.inStock && b.inStock, spread: b.size - a.size,
      };
      const same = (x, y) => Math.abs(x - y) < 1e-9;
      const better = !best
        || c.inStock > best.inStock
        || (c.inStock === best.inStock && (
          c.total < best.total - 1e-9
          || (same(c.total, best.total) && (
            c.spread < best.spread - 1e-9
            || (same(c.spread, best.spread) && c.price < best.price)))));
      if (better) best = c;
    }
  }
  if (!best) return null;
  // Each AC carries its share of the room, by what it gives at the hottest afternoon.
  const caps = best.units.map((v) => capAt(v, need.peakC));
  const sum = caps[0] + caps[1];
  best.units = best.units.map((v, i) => {
    const yearly = yearlyBill(v.model, ctx, caps[i] / sum);
    return { ...v, yearly, fiveYear: v.price + yearly * 5 };
  });
  best.yearly = best.units[0].yearly + best.units[1].yearly;
  return best;
}

// The biggest single AC, when it falls only a little short of the room.
// The answer stays two ACs, but this one is shown beside it, honestly: what
// it gives, what the room needs, and above what temperature it falls short.
// The customer decides.
const NEAR_SHARE = 0.9;
function nearSingle(need, ctx) {
  const capAt = (u, t) => HL.capacityTR(u.kw, u.full, t);
  const share = (u) => Math.min(capAt(u, need.peakC) / need.summerTR, capAt(u, need.monsoonC) / need.monsoonTR);
  const best = UNITS.reduce((a, u) => (!a || share(u) > share(a) ? u : a), null);
  if (!best || share(best) < NEAR_SHARE || share(best) >= 1) return null;
  const v = shopUnits([best], ctx)[0];
  if (!v) return null;
  const summerShort = capAt(best, need.peakC) / need.summerTR <= capAt(best, need.monsoonC) / need.monsoonTR;
  const givesTR = summerShort ? capAt(best, need.peakC) : capAt(best, need.monsoonC);
  const needTR = summerShort ? need.summerTR : need.monsoonTR;
  // The outside temperature above which it no longer covers the hottest-afternoon load.
  const rated = best.kw / HL.KW_PER_TR;
  const above = best.full + (1 - need.summerTR / rated) / (HL.DERATE_PER_C / 100);
  return {
    ...v,
    typeLabel: TYPE_LABEL[v.type],
    season: summerShort ? 'summer' : 'monsoon',
    givesTR,
    needTR,
    shortPct: Math.max(1, Math.round((1 - givesTR / needTR) * 100)),
    shortAboveC: summerShort && above > best.full && above < need.peakC ? Math.floor(above * 2) / 2 : null,
    monsoonOk: capAt(best, need.monsoonC) >= need.monsoonTR - 1e-9,
  };
}

export function recommend(need, ctx) {
  const ans = answerFor(ENGINE, need, UNITS);
  const c = { ...ctx, need };
  const noFixed = noFixedSpeed(need);
  if (ans.kind === 'team') return { mode: 'team', ans };
  if (ans.kind === 'pair') {
    const pair = bestPair(need, c);
    return pair ? { mode: 'pair', ans, pair, noFixed, near: nearSingle(need, c) } : { mode: 'team', ans };
  }

  // The sizes to choose from: the answer, plus the next size when only some
  // models of the answer keep up or it has no inverter; then bigger sizes
  // until something that fits is in stock.
  const idx = SIZES.indexOf(ans.size);
  const sizes = [ans.size];
  const fitIn = (list) => UNITS.filter((u) => list.includes(u.soldAs) && HL.fits(u, need));
  if (idx + 1 < SIZES.length && (ans.okNext.length || !ans.ok.some((u) => u.inverter))) sizes.push(SIZES[idx + 1]);
  let shop = shopUnits(fitIn(sizes), c);
  while (!shop.some((v) => v.inStock) && sizes.length + idx < SIZES.length) {
    sizes.push(SIZES[idx + sizes.length]);
    shop = shopUnits(fitIn(sizes), c);
  }

  // One AC of each kind, in stock first, then the best value.
  const slotOf = (type) => {
    const v = shop.filter((x) => x.type === type).sort(stockThenValue)[0];
    return v ? { type, label: TYPE_LABEL[type], v } : null;
  };
  const slot = { fixed: slotOf('fixed'), three: slotOf('three'), five: slotOf('five'), heat: slotOf('heat') };
  const order = ctx.heating ? ['heat', 'three', 'five'] : ['fixed', 'three', 'five'];
  const slots = order.map((t) => slot[t]).filter(Boolean);

  // The one we recommend: always in stock.
  const inStock = (s) => s && s.v.inStock;
  let rec = null;
  let why = 'value';
  if (ctx.heating && inStock(slot.heat)) { rec = slot.heat.v; why = 'heat'; }
  if (!rec && ctx.hours <= LOW_USE_HOURS && inStock(slot.fixed)) { rec = slot.fixed.v; why = 'lowuse'; }
  if (!rec) {
    const pool = ['fixed', 'three', 'five'].map((t) => slot[t]).filter(inStock).map((s) => s.v).sort(byValue);
    rec = pool[0] || null;
  }
  if (!rec && inStock(slot.heat)) { rec = slot.heat.v; why = 'heat'; }
  if (!rec) {
    // Nothing that fits is in stock anywhere: name the best, clearly marked as on order.
    rec = slots.map((s) => s.v).sort(byValue)[0] || null;
  }
  if (rec && !slots.some((s) => s.v.key === rec.key)) slots.unshift({ type: rec.type, label: TYPE_LABEL[rec.type], v: rec });
  if (rec) rec = { ...rec, typeLabel: TYPE_LABEL[rec.type] };

  // A non-inverter of this tonnage that cannot cool the room at the heat: name it, with what it gives.
  let shortFixed = null;
  if (!slot.fixed && !ctx.heating) {
    const f = UNITS.find((u) => !u.inverter && u.soldAs === ans.size);
    if (f) shortFixed = fixedGap(f, need);
  }

  // Which kind wins at each daily use, so each column can say "best if you
  // run it ..." — worked out on this room's own numbers, heating aside.
  const decide = (hrs) => {
    const c2 = { ...c, hours: hrs };
    const ok = (t) => (slot[t] && slot[t].v.inStock ? slot[t].v : null);
    if (hrs <= LOW_USE_HOURS && ok('fixed')) return 'fixed';
    const cost = (v) => v.price + 5 * yearlyBill(v.model, c2);
    return ['fixed', 'three', 'five'].filter(ok).sort((a, b) => cost(ok(a)) - cost(ok(b)))[0] || null;
  };
  const winsAt = { fixed: [], three: [], five: [] };
  [4, 8, 12, 16].forEach((h) => { const t = decide(h); if (t) winsAt[t].push(h); });
  const bestFor = Object.fromEntries(Object.entries(winsAt).map(([t, hs]) => [t, hoursText(hs)]));

  const res = {
    mode: 'single', ans, size: ans.size,
    rec,
    short: rec ? shortWhy(rec, why, c, ans) : '',
    sizeNote: rec && rec.size !== ans.size
      ? (shop.some((v) => v.inStock && v.size === ans.size)
        ? `One size up: it costs less over 5 years.`
        : `One size up: no ${tonLabel(ans.size)} that fits is in stock today.`)
      : '',
    recOnOrder: Boolean(rec && !rec.inStock),
    reasons: rec ? reasonsFor(rec, why, slots, c, ans, shop.some((v) => v.inStock && v.size === ans.size)) : [],
    picks: slots.map((s) => ({ ...s.v, typeLabel: s.label, bestFor: bestFor[s.type] || '', recommended: Boolean(rec && s.v.key === rec.key) })),
    shortFixed,
    noFixed: shortFixed || ctx.heating ? null : noFixed,
    count: UNITS.filter((u) => u.soldAs === ans.size).length,
  };
  // Big single sizes: show an in-stock pair when it is cheaper than the one we recommend.
  if (ans.size >= 2 && rec) {
    const pair = bestPair(need, c);
    if (pair && pair.inStock && (!rec.inStock || pair.price < rec.price)) res.altPair = pair;
  }
  return res;
}

// [4] → "up to 4 hrs a day", [8, 12] → "8 to 12 hrs a day", [12, 16] → "12 hrs a day or more".
function hoursText(hs) {
  if (!hs.length) return '';
  if (hs.length === 4) return 'any number of hours';
  const lo = hs[0];
  const hi = hs[hs.length - 1];
  if (lo === 4) return `up to ${hi} hrs a day`;
  if (hi === 16) return `${lo} hrs a day or more`;
  if (lo === hi) return `about ${lo} hrs a day`;
  return `${lo} to ${hi} hrs a day`;
}

// The one line shown up front. The full reasons sit behind "Why this one?".
function shortWhy(rec, why, ctx) {
  if (!rec.inStock) return 'Nothing that fits this room is in stock today: this is the best value on order.';
  if (why === 'heat') return 'Cools in summer and heats in winter.';
  if (why === 'lowuse') return `At ${ctx.hours} hrs a day a non-inverter is the smart buy: far cheaper to repair.`;
  if (rec.type === 'five') return `Lowest total cost over 5 years at ${ctx.hours} hrs a day: its lower bills cover the higher price.`;
  return `Lowest total cost over 5 years at ${ctx.hours} hrs a day, price plus electricity.`;
}

// A non-inverter that cannot cool the room: what it really gives at the heat.
function fixedGap(u, need) {
  const m = CATALOGUE.find((x) => x.id === u.id);
  return { size: u.soldAs, series: u.series, givesTR: HL.capacityTR(u.kw, u.full, need.peakC), image: m ? m.image : '' };
}

// The customer may want a non-inverter. When none of them covers the room,
// say why: the biggest one and what it really gives at the heat.
function noFixedSpeed(need) {
  const fixed = UNITS.filter((u) => !u.inverter);
  if (!fixed.length || fixed.some((u) => HL.fits(u, need))) return null;
  return fixedGap(fixed.reduce((a, b) => (b.kw > a.kw ? b : a)), need);
}

// The collection page that lists every AC of one size.
export function collectionFor(size) {
  return COLLECTIONS.find((c) => c.filter && Object.keys(c.filter).length === 1 && Number(c.filter.soldAs) === size) || null;
}

// Where the heat comes from, in four plain buckets (one season of heatLoad).
export function heatDrivers(season) {
  const L = season.lines;
  const rows = [
    { key: 'sun', label: 'Sun and glass', v: L.glassSolar + L.glassCond, color: '#1e3a8a' },
    { key: 'walls', label: 'Walls, roof and floors', v: L.walls + L.roof + L.floor + L.ceiling + L.partition, color: '#2563eb' },
    { key: 'people', label: 'People, lights, gadgets', v: L.people + L.lights + L.equip + season.peopleLat, color: '#60a5fa' },
    { key: 'air', label: 'Fresh air coming in', v: season.faSenRoom + season.faLatRoom + season.oaSen + season.oaLat, color: '#bfdbfe' },
  ];
  const total = rows.reduce((s, r) => s + r.v, 0);
  return rows
    .map((r) => ({ ...r, pct: total > 0 ? Math.round((r.v / total) * 100) : 0 }))
    .filter((r) => r.pct > 0);
}

// The sheet's lines, in the sheet's order, for the detailed breakdown.
export function sheetLines(s) {
  const L = s.lines;
  return [
    ['Sun through glass', L.glassSolar],
    ['Outside wall', L.walls],
    ['Roof', L.roof],
    ['Floor', L.floor],
    ['Ceiling', L.ceiling],
    ['Glass conduction', L.glassCond],
    ['Inside walls', L.partition],
    ['People', L.people],
    ['Equipment', L.equip],
    ['Lights', L.lights],
    ['Safety margin', s.rsh - s.subtotal],
    ['Duct and fan', s.duct + s.fan],
    ['Outside air, bypass (sensible)', s.faSenRoom],
    ['People (latent, with margin)', s.rlh],
    ['Outside air, bypass (latent)', s.faLatRoom],
    ['Outside air (sensible)', s.oaSen],
    ['Outside air (latent)', s.oaLat],
  ];
}
